import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Group, GroupMembershipInvited, IndividualMembership } from "@shared/models/group"
import FindDoc from "cc-frontend/helpers/find-doc"
import findMany from "cc-frontend/helpers/find-many"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import { isCachedMembershipActiveTeam, isGroupMembershipIndividual } from "cc-frontend/utils/group"
import { use } from "ember-could-get-used-to-this"
import { includes, isNil } from "lodash-es"
import * as R from "remeda" // tree-shaking supported!

interface Signature {
  Args: {
    row: IndividualMembership
    teamIds: Array<string>
    group: Group
  }
}

export default class GroupMembersTableTeamsCell extends Component<Signature> {
  @service declare modalManager: ModalManager
  @service("ember-store") declare store: EmberStore

  @use userSummary = new FindDoc(() => {
    let userId =
      this.args.row.type == "group-membership-accepted" ? this.args.row.attributes.userId : null
    return ["user-summary", userId]
  })

  @findMany("args.teamIds", "group") teams

  get teamMembershipIds() {
    if (this.args.row.type === "group-membership-accepted") {
      if (this.userSummary.content === null) return []
      return R.pipe(
        get(get(this.userSummary.content, "attributes"), "cachedMemberships"),
        R.filter(isCachedMembershipActiveTeam),
        R.filter((membership) => includes(this.args.teamIds, get(membership, "groupId"))),
        R.map((membership) => membership.groupId)
      )
    } else if (this.args.row.type === "group-membership-invited") {
      // We know this is true from the line above, so this next line is soley for Typescript
      let row = this.args.row as GroupMembershipInvited
      if (isNil(get(this.teams, "content"))) return []
      return R.pipe(
        this.teams.content,
        R.filter((team: Group) => {
          let membershipIds = R.pipe(
            get(team.attributes, "memberships"),
            R.map((membership) => {
              if (!isGroupMembershipIndividual(membership)) return null
              return membership.type === "group-membership-accepted"
                ? membership.attributes.userId
                : membership.attributes.email
            }),
            R.compact
          )
          return includes(membershipIds, row.attributes.email)
        }),
        R.map((team) => team.id)
      )
    } else {
      return
    }
  }

  @action
  openSelectTeamSlideout() {
    let email =
      this.args.row.type == "group-membership-accepted"
        ? this.userSummary?.content?.attributes.email
        : this.args.row.attributes.email
    if (email === undefined) return

    this.modalManager.openModal("group/select-teams", {
      userSummary: this.userSummary.content ? this.userSummary.content : null,
      group: this.args.group,
      teamIds: this.args.teamIds,
      email: email,
    })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Group::MembersTable::Cells::Teams": typeof GroupMembersTableTeamsCell
  }
}
